<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const payments = [
  { ref: 'INV-2024-00847', date: '2024-12-01', description: 'Credit Issuance Fee – CCI-2024-00089-001 (6,120 tCO2e × $0.10)', amount: 612.00, status: 'Paid', statusColor: 'green' },
  { ref: 'INV-2024-00312', date: '2024-03-05', description: 'PDD Submission Fee', amount: 750.00, status: 'Paid', statusColor: 'green' },
  { ref: 'INV-2024-00211', date: '2024-02-28', description: 'PIN Submission Fee', amount: 500.00, status: 'Paid', statusColor: 'green' },
  { ref: 'INV-2024-00092', date: '2024-02-01', description: 'PCN Submission Fee', amount: 200.00, status: 'Paid', statusColor: 'green' },
  { ref: 'INV-2024-00001', date: '2024-01-15', description: 'Developer Account Registration Fee', amount: 1000.00, status: 'Paid', statusColor: 'green' },
  { ref: 'INV-2025-00124', date: '2025-04-15', description: 'CCI Certificate Fee – Pending Issuance Request', amount: 50.00, status: 'Pending', statusColor: 'yellow' },
]

const statusClass = (c: string) => ({ green: 'bg-green-100 text-green-800', yellow: 'bg-yellow-100 text-yellow-800', red: 'bg-red-100 text-red-800' })[c] ?? 'bg-gray-100 text-gray-600'
const total = payments.filter(p => p.status === 'Paid').reduce((s, p) => s + p.amount, 0)
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">Payment History</h1>
                <p class="text-gray-600">All fees and invoices associated with your developer account and projects.</p>
              </div>
              <button class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                <i class="fas fa-download mr-2"></i>Export CSV
              </button>
            </div>
          </div>
        </section>

        <!-- Summary -->
        <div class="grid grid-cols-3 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-accent">${{ total.toLocaleString(undefined, { minimumFractionDigits: 2 }) }}</p>
            <p class="text-sm text-gray-600">Total Paid</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-yellow-600">$50.00</p>
            <p class="text-sm text-gray-600">Pending</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-primary">{{ payments.length }}</p>
            <p class="text-sm text-gray-600">Total Invoices</p>
          </div>
        </div>

        <!-- Invoices table -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
              <tr>
                <th class="px-5 py-3 text-left">Invoice</th>
                <th class="px-5 py-3 text-left">Date</th>
                <th class="px-5 py-3 text-left">Description</th>
                <th class="px-5 py-3 text-right">Amount (USD)</th>
                <th class="px-5 py-3 text-left">Status</th>
                <th class="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="p in payments" :key="p.ref" class="hover:bg-gray-50">
                <td class="px-5 py-3 text-xs text-gray-500 font-mono">{{ p.ref }}</td>
                <td class="px-5 py-3 text-gray-700">{{ p.date }}</td>
                <td class="px-5 py-3 text-gray-700">{{ p.description }}</td>
                <td class="px-5 py-3 text-right font-medium text-gray-800">${{ p.amount.toFixed(2) }}</td>
                <td class="px-5 py-3">
                  <span :class="['inline-flex px-2 py-0.5 rounded-full text-xs font-medium', statusClass(p.statusColor)]">{{ p.status }}</span>
                </td>
                <td class="px-5 py-3">
                  <button class="text-primary text-xs hover:underline flex items-center space-x-1">
                    <i class="fas fa-download"></i><span>Receipt</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </main>
  </div>
</template>
